import type {
  AssetsAssetElasticSchemaTypedWithIdNullable,
  AssetsAssetElasticSchemaWithIdNullable,
  AssetsCollectionElasticSchemaWithIdNullable,
  AssetsRelationSchemaWithId,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
  type SearchObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';

export const PERMISSIONS = ['read', 'write', 'delete', 'change-acl'] as const;

export type Permission = (typeof PERMISSIONS)[number];

// ACL permissions come off the API as plain strings, and the backend can add
// one before the frontend knows it.
export const isPermission = (value: string): value is Permission =>
  PERMISSIONS.some((permission) => permission === value);

// TODO change to union type as it's not an intersection in the real life
export type SearchItemEntity = Omit<
  AssetsAssetElasticSchemaWithIdNullable &
    AssetsCollectionElasticSchemaWithIdNullable,
  'status' | 'relations' | 'object_type' | 'permissions' | 'type'
> & {
  /**
   * Post assets are a sibling member of AssetElasticSchemaTyped rather than a
   * variant of the generic member, so the generic member's enum does not list
   * 'POST' — but search returns them through this same flattened shape. Taken
   * off the union so every asset type it can carry is covered.
   */
  type?: AssetsAssetElasticSchemaTypedWithIdNullable['type'];
  status?: 'ACTIVE' | 'HIDDEN' | 'DELETED' | null;
  /**
   * We mutate this field in some cases
   * @see assetRelationsCreate in actions.ts
   * @todo https://linear.app/iconik/issue/FE-6719
   */
  relations?: Partial<AssetsRelationSchemaWithId>[];
  object_type: SearchObjectType;
  permissions?: Permission[];
};

/**
 * Tables render some columns (title, thumbnail, media icon) only for rows that
 * are search items, but the table itself is generic over its row type. An id
 * and an assets/collections object_type are what those columns read.
 */
export const isSearchItemEntity = (item: unknown): item is SearchItemEntity =>
  typeof item === 'object' &&
  item !== null &&
  'id' in item &&
  typeof item.id === 'string' &&
  'object_type' in item &&
  (item.object_type === OBJECT_TYPE_ASSETS ||
    item.object_type === OBJECT_TYPE_COLLECTIONS);

/**
 * Search answers with the elastic schemas, whose relation and collection fields
 * are nullable, while SearchItemEntity is the normalized shape with them
 * required. Nothing normalizes in between a search response and these readers,
 * so the widening is stated here once rather than at each of them.
 */
export const asSearchItems = (objects: object[]): SearchItemEntity[] =>
  // biome-ignore lint/plugin: see above.
  objects as SearchItemEntity[];

export type SearchItemEntityNormalized = Omit<
  SearchItemEntity,
  'proxies' | 'files' | 'formats'
> & {
  proxies?: string[];
  files?: string[];
  formats?: string[];
};
